import React from 'react';
import {
    DatePicker,
    Form,
    Modal,
    Radio,
    RadioChangeEvent
} from 'antd';
import dayjs from 'dayjs';

import ErrorBoundary from '../ErrorBoundary';
import {dateTimeFormat} from '../../lib/time';
import {messageType, showMessages} from '../../helpers/message';
import {dstOffSetDateCheck, getBookingIncrement} from '../../helpers/booking';
import {formItemFullWidth, formStandaloneLayout} from '../../assets/styles';
import {BookingCreateFormProps} from '../../types/bookingTypes';
import {unsupportedBookingType} from '../../helpers/text';

const BookingCreateForm = ({
   startTime,
   endTime,
   bookingType,
   carInfo,
   locationInfo,
   isUpdatingBooking,
   toggleFormVisibility,
   syncStateWithBookingForm,
   goToBookingSummary,
   clearNewBooking,
}: BookingCreateFormProps) => {
    const [form] = Form.useForm();
    const mobileMode = window.innerWidth <= 768;
    const bookingIncrement = getBookingIncrement();
    const bookingIncrementMin = Number(bookingIncrement) / 60;

    const [loading, setLoading] = React.useState<boolean>(false);
    const [finish, setFinish] = React.useState<boolean>(false);
    const selectedBookingType = Form.useWatch('bookingType', form);

    // Populate the modal's form fields on open.
    React.useEffect(() => {
        form.setFieldsValue({
            'startTime': startTime,
            'endTime': endTime,
            'bookingType': bookingType,
        });
    }, [form, startTime, endTime, bookingType]);

    const onCancel = () => {
        resetTimeToOriginal();
        setLoading(false);
        toggleFormVisibility(false);
        clearNewBooking();
    };

    const isStartTimeBeforeEndTime = React.useCallback(() => {
        const startTime = form.getFieldValue('startTime');
        const endTime = form.getFieldValue('endTime');

        return startTime.isBefore(endTime);
    }, [form]);

    const resetTimeToOriginal = React.useCallback(() => {
        form.setFieldsValue({
            'startTime': dayjs(startTime).tz(),
            'endTime': dayjs(endTime).tz(),
        });
    }, [startTime, endTime, form]);

    const handleFinish = React.useCallback(() => {
        setLoading(true);

        const bookingType = form.getFieldValue('bookingType');

        // Additional data sanity check.
        let errorMessage = '';
        switch (bookingType) {
            case 'open_return':
            case 'set_return':
                if (!isStartTimeBeforeEndTime()) {
                    errorMessage = 'Start Time cannot be later than End Time!';
                }
                break;
            default:
                errorMessage = unsupportedBookingType;
                break;
        }

        if (errorMessage) {
            resetTimeToOriginal();
            showMessages(errorMessage, messageType.error);
        } else {
            // Bubble up selected booking info.
            const syncStatus = syncStateWithBookingForm({
                startTime: form.getFieldValue('startTime'),
                endTime: form.getFieldValue('endTime'),
                bookingType,
            });

            if (syncStatus) {
                goToBookingSummary();
            }
        }

        setLoading(false);
        toggleFormVisibility(false);
    }, [form, isStartTimeBeforeEndTime, resetTimeToOriginal, syncStateWithBookingForm, toggleFormVisibility, goToBookingSummary]);

    React.useEffect(() => {
        if (finish) {
            handleFinish();
        }
    }, [finish, handleFinish]);

    const onStartTimeChange = (newStart: dayjs.Dayjs | null) => {
        if (newStart) {
            const [start, end] = dstOffSetDateCheck({
                start: newStart,
                end: form.getFieldValue('endTime'),
                isStart: true,
                format: dateTimeFormat,
            });
            form.setFieldsValue({'startTime': start, 'endTime': end});
        }
    };

    const onEndTimeChange = (newEnd: dayjs.Dayjs | null) => {
        if (newEnd) {
            const [start, end] = dstOffSetDateCheck({
                start: form.getFieldValue('startTime'),
                end: newEnd,
                isStart: false,
                format: dateTimeFormat,
            });
            form.setFieldsValue({'startTime': start, 'endTime': end});
        }
    };

    // Disable dates earlier than today in date picker.
    const disabledDate = (input: dayjs.Dayjs) => {
        return input < dayjs().tz().subtract(1, 'days').endOf('day');
    };

    const datepickerProps = {
        showTime: {minuteStep: bookingIncrementMin, use12Hours: true, format: "hh:mm A"},
        disabledDate: disabledDate,
        format: dateTimeFormat,
        allowClear: false,
        inputReadOnly: mobileMode,
        style: {...formItemFullWidth},
    };

    // Set duration to 12h for open return.
    const onBookingTypeChange = (e: RadioChangeEvent) => {
        const type = e.target.value;

        if (type === 'open_return') {
            const startTime = form.getFieldValue('startTime');
            form.setFieldValue('endTime', startTime.add(12, 'hour'));
        }
    };

    return (
        <ErrorBoundary>
            <Modal
                title="Create Booking"
                open={isUpdatingBooking}
                confirmLoading={loading}
                onOk={() => {
                    form.validateFields().then(() => {
                        setFinish(true);
                    })
                }}
                onCancel={onCancel}
                okText='Save Changes'
                style={{minWidth: '50%'}}
            >
                <Form
                    {...formStandaloneLayout}
                    form={form}
                >
                    <Form.Item
                        name="bookingType"
                        label="Booking Type"
                    >
                        <Radio.Group
                            name="bookingType"
                            onChange={onBookingTypeChange}
                            buttonStyle="solid"
                        >
                            <Radio.Button value="set_return">Set Return</Radio.Button>
                            <Radio.Button value="open_return">Open Return</Radio.Button>
                        </Radio.Group>
                    </Form.Item>
                    <Form.Item
                        name="startTime"
                        label="Start Time"
                        rules={
                            [{type: 'object', required: true}]
                        }
                    >
                        <DatePicker
                            {...datepickerProps}
                            onChange={onStartTimeChange}
                        />
                    </Form.Item>
                    {
                        <Form.Item
                            name="endTime"
                            label="End Time"
                            rules={
                                [{
                                    type: 'object',
                                    required: true,
                                }]
                            }
                        >
                            <DatePicker
                                disabled={selectedBookingType === 'open_return'}
                                {...datepickerProps}
                                onChange={onEndTimeChange}
                            />

                        </Form.Item>
                    }
                    <Form.Item label="Car" wrapperCol={{lg: 12}}>
                        <span className="ant-form-text">{carInfo}</span>
                    </Form.Item>
                    <Form.Item label="Location" wrapperCol={{lg: 12}}>
                        <span className="ant-form-text">{locationInfo}</span>
                    </Form.Item>
                </Form>
            </Modal>
        </ErrorBoundary>
    );
}

export default BookingCreateForm;
